import React from 'react';
import { Box, Typography, FileUpload } from '@lucid/lego';
import { useFileUpload } from '../../../../../hooks/useFileUpload';
import styles from './DocumentsTab.module.scss';

type DocumentsTabProps = {
  theme: 'light' | 'dark';
  prospectId: string;
  customerId?: string;
};

/**
 * Documents tab component for prospect details
 * Allows file uploads and displays the API response for development reference
 * @component
 * @param props - Component props
 * @param props.theme - Theme for the component
 * @param props.prospectId - Prospect identifier
 * @param props.customerId - Customer identifier for file uploads
 * @returns JSX.Element
 */
const DocumentsTab: React.FC<DocumentsTabProps> = ({ theme, prospectId, customerId }) => {
  const { getUploadUrl, handleFileRemove } = useFileUpload({
    customerId: customerId || prospectId,
    category: 'generic',
    tags: { prospectId },
  });

  return (
    <Box pt={16} className={styles.documentsTab}>
      <Box mb={24}>
        <Typography variant="body" size="medium" theme={theme} color="white">
          Upload documents for this prospect. After successful upload, the API response will be
          displayed below for development reference.
        </Typography>
      </Box>

      <Box mb={24}>
        <FileUpload
          dataTestId="prospect-documents-upload"
          theme={theme}
          getUploadUrl={getUploadUrl}
          onFileRemove={handleFileRemove}
          maxFileSize={50 * 1024 * 1024}
          multiple
          accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx"
          label="Prospect Documents"
          helperText="JPG, PNG, GIF, PDF, DOC files supported (max 50 MB each)"
          showDevResponse
        />
      </Box>
    </Box>
  );
};

export default DocumentsTab;
